import { laterShort, laterShortMessage, mdy, NO_BALANCE_MESSAGE, type Projection } from "@scheduler/shared";

import { dateRange, hours } from "./format";

/**
 * The balance working — "you will have X, Y is already requested, Z is available" — shown the
 * same way to the person asking and to the person approving. "Already requested" is the PTO
 * BEFORE this request; the PTO after it is one line, covered or the one it would leave short
 * (punch list S28, Jeremy's wording, 2 Nov 2026).
 */
export function ProjectionLedger({ projection, requested }: { projection: Projection; requested: number }) {
  if (projection.kind === "UNLIMITED") return <p className="muted">Unlimited PTO — no balance check.</p>;
  if (projection.kind === "INELIGIBLE") return <p className="muted">Part-time: no PTO.</p>;
  if (projection.kind === "NO_BALANCE") return <p className="muted">{NO_BALANCE_MESSAGE}</p>;
  // Jury duty, bereavement and unpaid: a shortfall that does not apply would be wrong to show.
  // "the", not "your": HR sees this too, on the unpaid form and in Approvals.
  if (projection.kind === "NOT_CHARGED") return <p className="muted">This does not use the PTO balance.</p>;
  const pr = projection;
  const left = pr.available - requested;
  const squeezed = laterShort(pr.later, requested);

  return (
    <table className="ledger">
      <tbody>
        <tr>
          <td>
            Balance from payroll
            <span className="why">through {mdy(pr.anchor)}</span>
          </td>
          <td>{hours(pr.opening)}</td>
        </tr>
        <tr className="sub">
          <td>Accrual to {mdy(pr.target)}<span className="why">{pr.periods} pay {pr.periods === 1 ? "period" : "periods"} × {hours(pr.perPeriod)} h</span></td>
          <td>+{hours(pr.earned)}</td>
        </tr>
        {pr.capped ? (
          <tr className="sub"><td>Capped at 150% of the annual rate<span className="why">accrual stops at {hours(pr.cap)} h</span></td><td>= {hours(pr.gross)}</td></tr>
        ) : null}
        <tr className="total"><td>Projected balance</td><td>{hours(pr.gross)}</td></tr>
        {pr.reservedDetail.length ? (
          pr.reservedDetail.map((r) => (
            <tr key={r.id} className="sub"><td>Already requested<span className="why">{dateRange(r.start, r.end)}, {r.status.toLowerCase()}</span></td><td>−{hours(r.hours)}</td></tr>
          ))
        ) : pr.later.length ? (
          <tr className="sub"><td>Already requested</td><td>−0.0</td></tr>
        ) : (
          <tr className="sub"><td>Already requested<span className="why">no other future PTO</span></td><td>−0.0</td></tr>
        )}
        {squeezed ? (
          <tr className="sub"><td className="bad">{laterShortMessage(squeezed.item)}</td><td /></tr>
        ) : pr.later.length ? (
          <tr className="sub"><td>Later PTO still covered</td><td /></tr>
        ) : null}
        <tr className="total big"><td>Available</td><td>{hours(pr.available)}</td></tr>
        <tr><td>This request</td><td>−{hours(requested)}</td></tr>
        <tr className="total">
          <td>Left after</td>
          <td className={left < -1e-9 ? "bad" : "ok"}>{hours(left)}</td>
        </tr>
      </tbody>
    </table>
  );
}
